/**
 * Beauty Depot main product section
 *
 * Wires variant switching, fulfillment selection, CTA sublabel,
 * gallery thumb swapping, and the "You may also like" row.
 * All data comes from the section's JSON script — no extra fetches needed
 * for variants/prices. Recs are pulled from /recommendations/products.json.
 */

(function () {
  "use strict";

  const sections = document.querySelectorAll("[data-bd-pdp]");
  sections.forEach(initSection);

  function initSection(root) {
    const jsonNode = root.querySelector("[data-bd-product-json]");
    if (!jsonNode) return;

    let product;
    try {
      product = JSON.parse(jsonNode.textContent);
    } catch (e) {
      console.warn("[bd-pdp] bad product JSON", e);
      return;
    }

    const state = {
      variant: product.variants.find((v) => v.id === currentVariantId(root)) || product.variants[0],
      selected: null,
      fulfillment: "ship"
    };
    state.selected = (state.variant?.options || []).slice();

    wireThumbs(root);
    wireOptions(root, product, state);
    wireFulfillment(root, state);
    wireForm(root, product, state);
    wireMobileGallery(root);
    wireMobileCta(root);
    loadRecs(root);
  }

  function currentVariantId(root) {
    const hidden = root.querySelector("[data-bd-variant-id]");
    return hidden ? parseInt(hidden.value, 10) : null;
  }

  // ---------- Gallery ------------------------------------------------------

  function wireThumbs(root) {
    const main = root.querySelector("[data-bd-main-img]");
    if (!main) return;
    root.querySelectorAll("[data-bd-thumb]").forEach((btn) => {
      btn.addEventListener("click", () => {
        root.querySelectorAll("[data-bd-thumb]").forEach((b) =>
          b.classList.toggle("is-selected", b === btn)
        );
        const full = btn.getAttribute("data-full");
        if (full) main.src = full;
      });
    });
  }

  // ---------- Mobile swipe gallery ----------------------------------------

  function wireMobileGallery(root) {
    const slides = root.querySelector("[data-bd-mobile-slides]");
    const dots = root.querySelectorAll("[data-bd-mobile-dot]");
    if (!slides || dots.length === 0) return;

    const activeIdx = () => Math.round(slides.scrollLeft / slides.clientWidth);

    const sync = () => {
      const i = activeIdx();
      dots.forEach((d, di) => d.classList.toggle("is-active", di === i));
    };

    let tick;
    slides.addEventListener(
      "scroll",
      () => {
        cancelAnimationFrame(tick);
        tick = requestAnimationFrame(sync);
      },
      { passive: true }
    );

    dots.forEach((dot, i) => {
      dot.addEventListener("click", () => {
        slides.scrollTo({ left: i * slides.clientWidth, behavior: "smooth" });
      });
    });
  }

  // ---------- Mobile sticky CTA -------------------------------------------

  function wireMobileCta(root) {
    const bar = root.querySelector("[data-bd-mobile-cta]");
    const mainCta = root.querySelector("[data-bd-cta]");
    const form = root.querySelector(".bd-pdp__form");
    if (!bar || !mainCta || !form) return;

    const addBtn = bar.querySelector("[data-bd-mobile-add]");
    if (addBtn) {
      addBtn.addEventListener("click", () => {
        // Route through the main form so line-item properties (e.g. Auto-Replenish)
        // and the selected variant id come along.
        if (form.requestSubmit) {
          form.requestSubmit();
        } else {
          form.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
        }
      });
    }

    // Show the bar whenever the inline CTA scrolls out of view.
    if ("IntersectionObserver" in window) {
      const obs = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            bar.classList.toggle("is-visible", !entry.isIntersecting);
            bar.setAttribute("aria-hidden", entry.isIntersecting ? "true" : "false");
          });
        },
        { root: null, threshold: 0.01, rootMargin: "0px 0px -20% 0px" }
      );
      obs.observe(mainCta);
    } else {
      // Fallback: always show on mobile.
      bar.classList.add("is-visible");
      bar.setAttribute("aria-hidden", "false");
    }
  }

  // ---------- Options ------------------------------------------------------

  function wireOptions(root, product, state) {
    root.querySelectorAll("[data-bd-option-btn]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const idx = parseInt(btn.getAttribute("data-option-index"), 10);
        const value = btn.getAttribute("data-option-value");
        state.selected[idx] = value;

        // Flip selection inside this group only.
        const group = btn.closest(".bd-pdp__option-group");
        group.querySelectorAll("[data-bd-option-btn]").forEach((b) =>
          b.classList.toggle("is-selected", b === btn)
        );
        const label = group.querySelector("[data-bd-option-value-label]");
        if (label) label.textContent = value;

        // Resolve the matching variant.
        const match = product.variants.find((v) =>
          v.options.every((o, i) => o === state.selected[i])
        );
        if (match) {
          state.variant = match;
          updateForVariant(root, match);
          swapVariantImage(root, match);
        } else {
          // No matching variant — disable CTA.
          state.variant = null;
          setCtaState(root, { available: false, label: "Unavailable" });
        }
      });
    });
  }

  function updateForVariant(root, variant) {
    const hidden = root.querySelector("[data-bd-variant-id]");
    if (hidden) hidden.value = variant.id;

    const priceNode = root.querySelector("[data-bd-price]");
    const compareNode = root.querySelector("[data-bd-compare]");
    const saveNode = root.querySelector("[data-bd-save]");
    const bnplNode = root.querySelector("[data-bd-bnpl]");
    const subscribeNode = root.querySelector("[data-bd-subscribe]");

    if (priceNode) priceNode.textContent = money(variant.price);
    if (bnplNode) bnplNode.textContent = money(Math.round(variant.price / 4));
    if (subscribeNode)
      subscribeNode.textContent = money(Math.floor(variant.price * 0.95));

    const mobilePrice = root.querySelector("[data-bd-mobile-price]");
    if (mobilePrice) mobilePrice.textContent = money(variant.price);

    const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
    priceNode?.classList.toggle("is-sale", !!onSale);
    if (compareNode) {
      if (onSale) {
        compareNode.textContent = money(variant.compare_at_price);
        compareNode.style.display = "";
      } else {
        compareNode.style.display = "none";
      }
    }
    if (saveNode) {
      if (onSale) {
        const pct = Math.round(
          ((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100
        );
        saveNode.textContent = `Save ${pct}%`;
        saveNode.style.display = "";
      } else {
        saveNode.style.display = "none";
      }
    }

    setCtaState(root, {
      available: variant.available,
      label: variant.available ? "Add to Bag" : "Sold Out"
    });

    const stockText = root.querySelector("[data-bd-stock-text]");
    if (stockText)
      stockText.textContent = variant.available
        ? "In stock and ready to ship"
        : "Currently unavailable";

    // Sync URL param for shareable variant links.
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("variant", variant.id);
      window.history.replaceState({}, "", url);
    } catch (_) {}
  }

  function swapVariantImage(root, variant) {
    if (!variant.featured_image) return;
    const main = root.querySelector("[data-bd-main-img]");
    if (!main) return;
    const src = variant.featured_image.src;
    if (!src) return;
    // Shopify variant images often come back with -_large suffix stripped.
    main.src = src.replace(/\.(jpg|jpeg|png|webp)(\?.*)?$/i, "_1200x.$1$2");
    root.querySelectorAll("[data-bd-thumb]").forEach((btn) => {
      const full = btn.getAttribute("data-full") || "";
      const matches = full.includes(fileRoot(src));
      btn.classList.toggle("is-selected", matches);
    });

    // Also scroll the mobile carousel to the matching slide.
    const slides = root.querySelector("[data-bd-mobile-slides]");
    if (slides) {
      const imgs = slides.querySelectorAll(".bd-pdp__mobile-slide");
      const key = fileRoot(src);
      imgs.forEach((img, i) => {
        if ((img.getAttribute("src") || "").includes(key)) {
          slides.scrollTo({ left: i * slides.clientWidth, behavior: "smooth" });
        }
      });
    }
  }

  function fileRoot(url) {
    return (url.split("/").pop() || "").split(".")[0];
  }

  function setCtaState(root, { available, label }) {
    const cta = root.querySelector("[data-bd-cta]");
    const main = root.querySelector("[data-bd-cta-main]");
    if (cta) {
      cta.toggleAttribute("disabled", !available);
      cta.classList.toggle("is-disabled", !available);
    }
    if (main && label) main.textContent = label;

    const mobileBtn = root.querySelector("[data-bd-mobile-add]");
    if (mobileBtn) {
      mobileBtn.toggleAttribute("disabled", !available);
      if (label) mobileBtn.textContent = label;
    }
  }

  // ---------- Fulfillment cards -------------------------------------------

  function wireFulfillment(root, state) {
    const group = root.querySelector("[data-bd-fulfillment-group]");
    if (!group) return;
    group.querySelectorAll("[data-bd-fulfillment]").forEach((card) => {
      card.addEventListener("click", () => {
        state.fulfillment = card.getAttribute("data-bd-fulfillment");
        group.querySelectorAll("[data-bd-fulfillment]").forEach((c) =>
          c.classList.toggle("is-selected", c === card)
        );
        const sub = root.querySelector("[data-bd-cta-sub]");
        if (sub) sub.textContent = ctaSubLabel(state.fulfillment);
      });
    });
  }

  function ctaSubLabel(mode) {
    if (mode === "pickup") return "Get It In-Store";
    if (mode === "sameday") return "Get It Today";
    return "Get It Shipped";
  }

  // ---------- Form submit (AJAX add to cart) ------------------------------

  function wireForm(root, product, state) {
    const form = root.querySelector(".bd-pdp__form");
    if (!form) return;

    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      if (!state.variant?.available) return;
      const cta = root.querySelector("[data-bd-cta]");
      cta?.classList.add("is-loading");

      try {
        const fd = new FormData(form);
        const res = await fetch("/cart/add.js", {
          method: "POST",
          body: fd,
          headers: { Accept: "application/json" },
          credentials: "same-origin"
        });
        if (!res.ok) throw new Error("add failed: " + res.status);

        toast("Added to bag");
        document.dispatchEvent(new CustomEvent("cart:refresh"));

        // Bump common cart badges without assuming a specific theme.
        const cart = await fetch("/cart.js", { credentials: "same-origin" }).then((r) => r.json());
        document
          .querySelectorAll("[data-cart-count], .cart-count-bubble span, .cart-link__bubble-num")
          .forEach((n) => (n.textContent = String(cart.item_count)));
      } catch (err) {
        console.warn("[bd-pdp]", err);
        toast("Something went wrong. Please try again.", true);
      } finally {
        cta?.classList.remove("is-loading");
      }
    });
  }

  // ---------- Recommendations ---------------------------------------------

  async function loadRecs(root) {
    const recs = root.querySelector("[data-bd-recs]");
    if (!recs) return;
    const productId = recs.getAttribute("data-product-id");
    const limit = recs.getAttribute("data-limit") || 6;
    try {
      const data = await fetch(
        `/recommendations/products.json?product_id=${productId}&limit=${limit}`,
        { credentials: "same-origin" }
      ).then((r) => r.json());
      const list = data.products || [];
      if (list.length === 0) {
        recs.remove();
        return;
      }
      const grid = recs.querySelector("[data-bd-recs-grid]");
      grid.innerHTML = list.map(recCard).join("");
    } catch (_) {
      recs.remove();
    }
  }

  function recCard(p) {
    const img =
      p.featured_image ||
      (p.images && p.images[0]) ||
      "https://cdn.shopify.com/s/images/admin/no-image-compact.gif";
    const price = typeof p.price === "number" ? money(p.price) : p.price ? money(p.price) : "";
    const url = p.url || `/products/${p.handle}`;
    return `
      <a class="bd-pdp__rec-card" href="${escapeAttr(url)}">
        <div class="bd-pdp__rec-img-wrap">
          <img class="bd-pdp__rec-img" src="${escapeAttr(img)}" alt="${escapeAttr(p.title || "")}" loading="lazy">
        </div>
        <div class="bd-pdp__rec-vendor">${escapeHtml(p.vendor || "")}</div>
        <div class="bd-pdp__rec-title">${escapeHtml(p.title || "")}</div>
        <div class="bd-pdp__rec-price">${escapeHtml(price)}</div>
      </a>
    `;
  }

  // ---------- Utils --------------------------------------------------------

  function money(cents) {
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents, window.Shopify.money_format || "${{amount}}");
    }
    return "$" + (cents / 100).toFixed(2);
  }

  function toast(msg, isError) {
    const t = document.createElement("div");
    t.className = "bd-pdp__toast" + (isError ? " is-error" : "");
    t.textContent = msg;
    document.body.appendChild(t);
    requestAnimationFrame(() => t.classList.add("is-in"));
    setTimeout(() => {
      t.classList.remove("is-in");
      setTimeout(() => t.remove(), 300);
    }, 2200);
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, (c) => ({
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#39;"
    }[c]));
  }
  function escapeAttr(s) {
    return escapeHtml(s);
  }
})();
